import "./notificationCampaigns.scss";
import "assets/styles/table.scss";
import "components/HomePage/home.scss";
import { useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import { Ring } from "@uiball/loaders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faUsers,
    faCircleCheck,
    faUserSlash,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { BRAND } from "constants/brand";
import { Toast } from "primereact/toast";
import SelectField from "components/Ui/SelectField/SelectField";
import useCity from "hooks/useCity";
import TableBodyCells from "components/Ui/TableBodyCells/TableBodyCells";
import TableHead from "components/Ui/TableHead/TableHead";
import {
    useNotificationCampaign,
    useNotificationCampaignRecipients,
    useResendNotificationCampaignGap,
} from "hooks/useNotificationCampaigns";
import { CampaignStat } from "./SendNotification";
import {
    notificationFailureLabel,
    notificationOutcomeLabel,
    notificationStatusLabel,
    notificationTargetLabel,
    numberFormatter,
} from "./notificationCampaignView";
import { formatDateTime } from "utils/dateFormat";

const OUTCOME_OPTIONS = [
    { value: "", label: "كل النتائج" },
    { value: "accepted", label: "تم قبوله" },
    { value: "no_device", label: "بدون جهاز مسجل" },
    { value: "failed", label: "فشل" },
    { value: "pruned", label: "جهاز غير صالح" },
    { value: "retrying", label: "ستتم المحاولة مجددًا" },
];

/**
 * NO "النظام" COLUMN, AND NO PLATFORM FILTER. The API stores `platform` but
 * never writes anything except "unknown" — the device row carries no platform
 * yet (talabatk/api#113). A column that reads «غير معروف» on every row, and a
 * filter whose four other options always return nothing, tell the operator
 * less than no column at all. Both come back with the platform data.
 */
const RECIPIENT_COLUMNS = [
    { id: "account", label: "الحساب", width: "220px" },
    { id: "outcome", label: "النتيجة", width: "150px" },
    { id: "failure", label: "سبب الفشل", width: "220px" },
    { id: "attempts", label: "المحاولات", width: "100px" },
];

const valueOption = (options, value) => options.find((option) => option.value === value) || null;

// Numbers only, 24 hour, the same shape `utils/orderTiming.js` already uses.
// `dateStyle: "medium"` wrote an English month name into an Arabic page, and
// an Arabic locale put a "م"/"ص" marker inside a left-to-right run, which the
// browser then reordered into something unreadable.
const formatDate = (value) => formatDateTime(value) ?? "—";

const NotificationCampaignDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);
    const [recipientPage, setRecipientPage] = useState(1);
    const [outcome, setOutcome] = useState("");
    const { campaign, loading, error } = useNotificationCampaign(id);
    const { cities } = useCity();
    const recipientParams = {
        page: recipientPage,
        size: 20,
        ...(outcome ? { outcome } : {}),
    };
    const {
        recipients,
        totalPages,
        loading: recipientsLoading,
    } = useNotificationCampaignRecipients(id, recipientParams, {
        poll: ["queued", "processing"].includes(campaign?.status),
    });
    const resendGap = useResendNotificationCampaignGap();

    if (loading) {
        return (
            <div className="campaign-page campaign-page--loading">
                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
            </div>
        );
    }

    if (error || !campaign) {
        return (
            <div className="campaign-page campaign-page--error campaign-card">
                <p>تعذر تحميل الحملة.</p>
                <NavLink className="button" to="/send-notification">
                    العودة إلى سجل الإرسال
                </NavLink>
            </div>
        );
    }

    const showMessage = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 4000 });
    };

    const resend = async () => {
        try {
            await resendGap.mutateAsync(campaign.id);
            showMessage("success", "بدأت إعادة الإرسال", "سيتم استهداف الأجهزة التي فشلت فقط");
        } catch (resendError) {
            showMessage(
                "error",
                "تعذرت إعادة الإرسال",
                resendError?.response?.data?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    return (
        <div className="campaign-page">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {/* The same shape as the review detail page: a small back control
                at the start of the row, the page's name beside it, and the one
                thing you can do to it at the far end. */}
            {/* The same shape as components/Reviews/ReviewDetail: a small
                outlined back button at the start of the row, the page's name
                beside it, and the campaign's state at the far end. */}
            <div className="campaign-detail__bar">
                <div className="campaign-detail__lead">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate(-1)}
                    >
                        <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" /> العودة
                    </button>
                    <h3>
                        حملة <bdi dir="ltr">#{campaign.id}</bdi>
                    </h3>
                </div>
                <span className={`campaign-status campaign-status--${campaign.status}`}>
                    {notificationStatusLabel(campaign.status)}
                </span>
            </div>

            <section className="campaign-card campaign-detail">
                {/* <bdi>, not dir="auto". An admin writes the title, so it can
                    be Latin — and dir="auto" on the heading itself turned the
                    whole block left-to-right, so a title like "fgregfref" sat
                    on the left of an Arabic page. <bdi> isolates the text's own
                    reading order and leaves the block where it belongs. */}
                <h3 className="campaign-detail__title">
                    <bdi>{campaign.title}</bdi>
                </h3>
                <p className="campaign-detail__audience">
                    <bdi>{notificationTargetLabel(campaign.target, cities ?? [])}</bdi>
                </p>
                <p className="campaign-detail__meta">
                    <span>
                        أُرسلت <bdi dir="ltr">{formatDate(campaign.createdAt)}</bdi>
                    </span>
                    {campaign.createdBy?.name ? (
                        <span>بواسطة {campaign.createdBy.name}</span>
                    ) : null}
                </p>

                <div className="home-stats-grid campaign-detail__stats">
                    <CampaignStat
                        icon={<FontAwesomeIcon icon={faUsers} />}
                        value={campaign.totalUsers}
                        label="الحسابات"
                    />
                    <CampaignStat
                        tone="money"
                        icon={<FontAwesomeIcon icon={faCircleCheck} />}
                        value={campaign.acceptedTokens}
                        label="أجهزة استلمت"
                    />
                    <CampaignStat
                        icon={<FontAwesomeIcon icon={faUserSlash} />}
                        value={campaign.noDeviceUsers}
                        label="حسابات بدون جهاز"
                    />
                    <CampaignStat
                        tone="deleted"
                        icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                        value={campaign.failedTokens}
                        label="أجهزة فشلت"
                    />
                </div>

                {campaign.status === "complete" && campaign.failedTokens > 0 ? (
                    <div className="campaign-detail__actions">
                        <button
                            type="button"
                            className="button"
                            onClick={resend}
                            disabled={resendGap.isPending}
                        >
                            إعادة الإرسال إلى الأجهزة التي فشلت
                        </button>
                        <span>
                            {`${numberFormatter.format(campaign.failedTokens)} جهازًا فشل إرساله. الحسابات بدون جهاز مسجل لا يمكن إعادة الإرسال إليها.`}
                        </span>
                    </div>
                ) : null}
            </section>

            <section className="campaign-card campaign-results">
                <div className="campaign-card__heading">
                    <h3>نتائج الحملة</h3>
                    <div className="campaign-results__filters">
                        <SelectField
                            value={valueOption(OUTCOME_OPTIONS, outcome)}
                            options={OUTCOME_OPTIONS}
                            onChange={(option) => {
                                setOutcome(option.value);
                                setRecipientPage(1);
                            }}
                        />
                    </div>
                </div>

                <div className="table-container">
                    <table>
                        <TableHead visibleColumns={RECIPIENT_COLUMNS} />
                        <tbody>
                            {recipientsLoading ? (
                                <tr>
                                    <td colSpan={RECIPIENT_COLUMNS.length}>
                                        جارٍ تحميل النتائج...
                                    </td>
                                </tr>
                            ) : recipients.length === 0 ? (
                                <tr>
                                    <td colSpan={RECIPIENT_COLUMNS.length}>
                                        <h4 className="campaign-history__empty">
                                            لا توجد نتائج تطابق الفلاتر
                                        </h4>
                                    </td>
                                </tr>
                            ) : (
                                recipients.map((recipient) => {
                                    const cellRenderers = {
                                        account: () => (
                                            <td>
                                                {recipient.user
                                                    ? `${recipient.user.name} (#${recipient.user.id})`
                                                    : "حساب محذوف"}
                                            </td>
                                        ),
                                        outcome: () => (
                                            <td>{notificationOutcomeLabel(recipient.outcome)}</td>
                                        ),
                                        failure: () => (
                                            <td>
                                                {notificationFailureLabel(recipient.failureCode)}
                                            </td>
                                        ),
                                        attempts: () => (
                                            <td>{numberFormatter.format(recipient.attempts)}</td>
                                        ),
                                    };
                                    return (
                                        <tr className="item" key={recipient.id}>
                                            <TableBodyCells
                                                visibleColumns={RECIPIENT_COLUMNS}
                                                renderers={cellRenderers}
                                            />
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 ? (
                    <div className="campaign-page__pagination">
                        <Pagination
                            count={totalPages}
                            page={recipientPage}
                            onChange={(_event, page) => setRecipientPage(page)}
                            color="primary"
                            shape="rounded"
                        />
                    </div>
                ) : null}
            </section>
        </div>
    );
};

export default NotificationCampaignDetails;
